import React, {useState} from 'react';
import styled, {css} from 'styled-components';
import {useDocumentTitle} from '@bootcamp/shared/src/util/hooks';

import {useBootcampConfigContext} from '@bootcamp/web/src/contexts/BootcampConfig';
import {useUserDataContext} from '@bootcamp/web/src/contexts/UserData';

import Container from '@bootcamp/web/src/bootcamps/pages/PageContainer';
import Overview from '@bootcamp/web/src/bootcamps/components/Cards/Overview';
import Breadcrumbs from '@bootcamp/web/src/components/NavBar/components/Breadcrumbs';
import Download from '@bootcamp/web/src/bootcamps/components/Cards/Download';

import {DownloadButton} from '@bootcamp/web/src/bootcamps/components/Cards/components/Buttons';
import theme from '@bootcamp/shared/src/styles/theme';

import Callout from '@bootcamp/web/src/bootcamps/components/Cards/Callout';
import {Info} from '@styled-icons/fluentui-system-filled/Info';

import {Row} from '@bootcamp/web/src/components/Grid';
import firstBy from 'thenby';

import {Label3} from '../../../components/Typography/next';
import {Star} from '@styled-icons/fluentui-system-filled/Star';

const StarIcon = styled(Star)`
  width: 16px;
  height: 16px;
  color: ${({theme}) => theme.colors.interfacePalette.yellow.alert};
`;

const StyledBreadcrumbs = styled(Breadcrumbs)`
  margin-bottom: ${({theme}) => theme.layouts.spacing.s};
`;
const StyledOverview = styled(Overview)`
  background: transparent;
  box-shadow: none;
  border: none;
  padding: 0;
  margin-bottom: 16px;
`;
const Icon = styled.img`
  width: 24px;
  height: 24px;
  margin: auto;
`
const SectionSelectorContainer = styled(Row)`
  position: relative;
  flex-direction: column;
  gap: 8px;

  ${({theme}) => theme.mediaQueries.tablet} {
    flex-direction: row;
    flex-wrap: wrap;
  }
`;
const SectionSelectorOption = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  width: 100%;
  border-radius: 8px;
  background: #FFFFFF1A;
  cursor: pointer;
  position: relative;

  ${({active}) => active && css`
    background: white;
    p {
      font-weight: 700;
      color: #2F3037;
    }
  `}

  ${({theme}) => theme.mediaQueries.tablet} {
    width: auto;
  }
  `;
const SectionSelectorIconWrapper = styled.div`
  margin-right: 4px;
  display: flex;

  img {
    width: 16px;
    height: 16px;
  }
  `;
const SectionSelectorText = styled(Label3)`
  color: white;
  text-transform: capitalize;
  font-weight: 400;
  position: relative;
  top: 1px;
`;
const Wrapper = styled.div`
  display: flex;
  flex-direction: row;

  ${({theme}) => theme.mediaQueries.tablet} {
    flex-direction: column;
  }
`;
const SelectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px;
  background: ${({theme}) => theme.colors.elevationPalette.surfaceTransparent};
  margin: 0 16px auto 0;
  border-radius: 8px;

  ${({theme}) => theme.mediaQueries.tablet} {
    margin: 0 0 16px 0;
  }
`;
const SelectionText = styled(Label3)`
  text-transform: uppercase;
  font-size: 12px;
  color: ${({theme}) => theme.colors.typographyPalette.transparent};
  margin-bottom: 8px;
`;
const ItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;
const StyledDownload = styled(Download)`
  height: auto;
`;

const CheatSheets = ({
  history,
  items,
  subTitle,
  body,
  title,
  comingSoon,
  categories,
}) => {
  const [activeTab, setActiveTab] = useState('all');
  const {config} = useBootcampConfigContext();
  const {isUpgraded} = useUserDataContext();

  useDocumentTitle(`${config.meta.siteTitle} | Cheat Sheets`);
  if (!isUpgraded) {
    items.sort(firstBy('freeAccess', {direction: 'desc'}));
  }
  const filteredItems = items.filter(item => activeTab === 'all' || activeTab === item.category);

  return (
    <Container>
      <StyledBreadcrumbs history={history} lockAtDepth={2} />
      <StyledOverview
        subTitle={subTitle}
        body={body}
        title={title}
        showProgress={false}
      />
      {comingSoon &&
        <Callout icon={<Info size={20} color={'white'} style={{marginRight: 8}} />}>
          {`<strong>More coming soon!</strong> Cheat Sheets will be released as they become available.`}
        </Callout>
      }
      <Wrapper>
        <SelectionWrapper>
          <SelectionText>filter by subject</SelectionText>
          <SectionSelectorContainer>
            {['all', ...categories].map(categoryTitle => (
              <SectionSelectorOption
                key={categoryTitle}
                active={activeTab === categoryTitle}
                onClick={() => setActiveTab(categoryTitle)}>
                <SectionSelectorIconWrapper>
                  {categoryTitle === 'all'
                    ? <StarIcon />
                    : <Icon src={items.find(({category}) => categoryTitle === category)?.iconUrl} />
                  }
                </SectionSelectorIconWrapper>
                <SectionSelectorText>
                  {categoryTitle}
                </SectionSelectorText>

              </SectionSelectorOption>
            ))}
          </SectionSelectorContainer>
        </SelectionWrapper>
        <ItemWrapper>
          {filteredItems.map((item, index) => (
            <StyledDownload
              key={`${item.name}-${index}`}
              name={item.name}
              description={item.description}
              themePalette={theme.colors.brandPalette.royal}
              locked={!(item.freeAccess || isUpgraded)}
              onClick={() => window.open(item.route, '_blank')}
              icon={<Icon src={item.iconUrl}/>}
              actions={[
                <DownloadButton />
              ]}
            />
          ))}
        </ItemWrapper>
      </Wrapper>
    </Container>
  );
}

export default CheatSheets;
